import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import useAxios from "@hooks/use-axios";
import {
    AFFILIATES_DEFAULT_LIMIT,
    DEFAULT_INVOICE_TERM_DAYS,
    SUPERADMIN_ENDPOINTS
} from "../misc/constants";
import { CURRENCY } from "@constants/panel/common";
import { defaultAffiliateFilters, invoiceSchema, toAffiliateFilterParams, toInvoicePayload } from "../misc/schema";
import { newExtInvoiceId, newInvoiceNumber } from "../misc/ids";
import { toRows } from "../misc/response";
import { toastify } from "@helpers/toast";

const CLOSED_MODAL = { open: false, affiliate: null };

// India's financial year, the shape the sample payload uses ("2023-24"). April
// opens the new one, so anything before it still belongs to the year before.
const currentPayoutPeriod = () => {
    const now = dayjs();
    const startYear = now.month() >= 3 ? now.year() : now.year() - 1;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

// Step 4: raise an invoice against a publisher. The brand is what makes both
// halves of that possible — its id scopes the affiliate list that supplies
// `holder_id`, and its `api_key` is what the invoice endpoint authenticates
// with (as a query param, not as a bearer). So nothing here can be asked for
// until a brand is chosen.
const useSuperadminInvoices = () => {
    const { axiosInstance, loading } = useAxios({ base: "base", alertTypeOnSuccess: "toast" });
    // No `initialLoader` here on purpose: the first fetch only happens once a
    // brand is picked, and a loader raised before any request would have
    // nothing to lower it — the table would spin for ever on an empty tab.
    const { axiosInstance: listInstance, loading: listLoading } = useAxios({ base: "base" });
    const { axiosInstance: brandInstance } = useAxios({ base: "base" });

    const [brands, setBrands] = useState([]);
    const [brandId, setBrandId] = useState("");
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: AFFILIATES_DEFAULT_LIMIT });
    const [filters, setFilters] = useState(defaultAffiliateFilters);
    const [modal, setModal] = useState(CLOSED_MODAL);
    const [raised, setRaised] = useState([]);

    const methods = useForm({
        resolver     : yupResolver(invoiceSchema),
        defaultValues: invoiceSchema.getDefault()
    });

    const selectedBrand = useMemo(
        () => brands.find((brand) => brand._id === brandId) || null,
        [brands, brandId]
    );

    const brandOptions = useMemo(
        () => brands.map((brand) => ({ value: brand._id, label: brand.name || brand.ext_id, status: brand.status })),
        [brands]
    );

    const fetchBrands = async () => {
        try {
            const res = await brandInstance.get(SUPERADMIN_ENDPOINTS.BRANDS);
            setBrands(toRows(res));
        } catch {
            setBrands([]);
        }
    };

    const fetchAffiliates = async (page = 1, limit = pagination.limit, currentFilters = filters, accountId = brandId) => {
        // The account id is a path segment, so there is no list to ask for yet.
        if (!accountId) {
            setRows([]);
            setPagination({ total: 0, page: 1, limit });
            return;
        }
        try {
            const params = await toAffiliateFilterParams(currentFilters);
            const res = await listInstance.get(SUPERADMIN_ENDPOINTS.AFFILIATES(accountId), { params: { page, limit, ...params } });
            const result = res?.data ?? res;
            setRows(result.items || []);
            setPagination({
                total: result.total ?? 0,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
        } catch {
            setRows([]);
            setPagination({ total: 0, page, limit });
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    useEffect(() => {
        fetchAffiliates(1, pagination.limit, filters, brandId);
    }, [filters, brandId]);

    const handlePageChange = (limit, page) => fetchAffiliates(page, limit, filters, brandId);
    const handlePerRowsChange = (limit) => fetchAffiliates(1, limit, filters, brandId);
    const handleFilterChange = (nextFilters) => setFilters((current) => ({ ...current, ...nextFilters }));
    const refresh = () => fetchAffiliates(pagination.page, pagination.limit, filters, brandId);

    const openInvoice = (affiliate) => {
        const issueDate = dayjs();
        methods.reset({
            ...invoiceSchema.getDefault(),
            payout_period : currentPayoutPeriod(),
            invoice_number: newInvoiceNumber(),
            ext_invoice_id: newExtInvoiceId(),
            currency      : CURRENCY,
            issue_date    : issueDate.format("YYYY-MM-DD"),
            due_date      : issueDate.add(DEFAULT_INVOICE_TERM_DAYS, "day").format("YYYY-MM-DD"),
            description   : `${issueDate.format("MMM YYYY")} affiliate payout`
        });
        setModal({ open: true, affiliate });
    };

    const closeInvoice = () => setModal(CLOSED_MODAL);

    const submitInvoice = methods.handleSubmit(async (values) => {
        // The api_key is the whole authentication for this call, so a missing
        // brand is stopped here rather than sent as `?api_key=undefined`.
        if (!selectedBrand?.api_key) {
            toastify({ title: "Pick a brand first — its api_key authenticates the invoice call.", type: "error" });
            return;
        }

        // The invoice's `holder_id` is the row's `_id` — the affiliate record
        // this list returns, not the row's own `holder_id` field.
        if (!modal.affiliate?._id) {
            toastify({ title: "This publisher row came back without an _id.", type: "error" });
            return;
        }

        const payload = toInvoicePayload(values, modal.affiliate._id);
        await axiosInstance.post(SUPERADMIN_ENDPOINTS.INVOICES, payload, {
            params: { api_key: selectedBrand.api_key }
        });

        setRaised((current) => [
            {
                id       : values.ext_invoice_id,
                number   : values.invoice_number,
                publisher: modal.affiliate.holder_name || modal.affiliate.email,
                amount   : Number(values.amount),
                currency : values.currency
            },
            ...current
        ]);
        closeInvoice();
        refresh();
    });

    const dismissRaised = (id) => setRaised((current) => current.filter((row) => row.id !== id));

    return {
        brandOptions,
        brandId,
        setBrandId,
        selectedBrand,
        rows,
        pagination,
        loading             : loading || listLoading,
        filters,
        handleFilterChange,
        handlePageChange,
        handlePerRowsChange,
        refresh,
        modal,
        methods,
        openInvoice,
        closeInvoice,
        submitInvoice,
        raised,
        dismissRaised,
        regenerateInvoiceIds: () => {
            methods.setValue("invoice_number", newInvoiceNumber(), { shouldValidate: true });
            methods.setValue("ext_invoice_id", newExtInvoiceId(), { shouldValidate: true });
        }
    };
};

export default useSuperadminInvoices;
